import { type CurrencyCode, minorUnitOf } from "./currency.ts";
import { type Money } from "./money.ts";
import { DEFAULT_ROUNDING, divideRounded, pow10, type RoundingMode } from "./rounding.ts";

/**
 * Exchange rates are stored as `bigint` on `core_stores.exchange_rate` and
 * snapshotted onto `pos_orders.exchange_rate`. The scale is fixed and shared so a
 * stored integer means the same thing everywhere.
 *
 * Six decimal places is the FX convention and covers the pairs this product
 * sees: 1 USD = 4100 KHR is `4_100_000000n`.
 */
export const EXCHANGE_RATE_SCALE = 6;

const RATE_DIVISOR = pow10(EXCHANGE_RATE_SCALE);

/** Parses "4100.5" into the scaled integer a store row holds. */
export function parseExchangeRate(decimal: string): bigint {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(decimal.trim());
  if (!match) throw new SyntaxError(`parseExchangeRate: not a positive decimal: ${decimal}`);
  const [, whole, fraction = ""] = match;
  if (fraction.length > EXCHANGE_RATE_SCALE) {
    throw new RangeError(
      `parseExchangeRate: ${decimal} exceeds ${EXCHANGE_RATE_SCALE} decimal places`,
    );
  }
  return BigInt(`${whole}${fraction.padEnd(EXCHANGE_RATE_SCALE, "0")}`);
}

export function formatExchangeRate(rate: bigint): string {
  const whole = rate / RATE_DIVISOR;
  const fraction = (rate % RATE_DIVISOR).toString().padStart(EXCHANGE_RATE_SCALE, "0");
  return `${whole}.${fraction}`.replace(/\.?0+$/, "");
}

/**
 * Converts `m` into `to` at `rate` units of `to` per unit of `m.currency`.
 *
 * The two currencies' minor units differ — USD has 2, KHR has 0 — so the
 * exponent difference is part of the conversion, not an afterthought. Callers
 * pass the exponents explicitly when working from an order's snapshot rather
 * than from today's registry.
 */
export function convert(
  m: Money,
  to: CurrencyCode,
  rate: bigint,
  options: {
    mode?: RoundingMode;
    fromMinorUnit?: number;
    toMinorUnit?: number;
  } = {},
): Money {
  if (rate <= 0n) throw new RangeError("convert: rate must be positive");
  const {
    mode = DEFAULT_ROUNDING,
    fromMinorUnit = minorUnitOf(m.currency),
    toMinorUnit = minorUnitOf(to),
  } = options;

  const exponentDelta = toMinorUnit - fromMinorUnit;
  let numerator = m.amount * rate;
  let denominator = RATE_DIVISOR;

  if (exponentDelta > 0) numerator *= pow10(exponentDelta);
  else if (exponentDelta < 0) denominator *= pow10(-exponentDelta);

  return { amount: divideRounded(numerator, denominator, mode), currency: to };
}
